import { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock, User, Phone, Calendar, MapPin, Users, Heart, Handshake, Camera, X } from "lucide-react";
import Logo from "@/components/Logo";

const SERVICE_MODES = [
  { id: "FIND_FRIEND", label: "Find a Friend", icon: Users, desc: "Make new friends locally" },
  { id: "DATE_FRIEND", label: "Date a Friend", icon: Heart, desc: "Open to dating connections" },
  { id: "RENT_FRIEND", label: "Rent a Friend", icon: Handshake, desc: "Offer paid companionship" },
];

function fmtDetail(d) {
  if (d == null) return "Something went wrong.";
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  if (d?.msg) return d.msg;
  return String(d);
}

export default function Signup() {
  const { setUser, refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", password: "", confirm: "",
    dob: "", city: "", service_mode: "", accept_terms: false,
  });
  const [photo, setPhoto] = useState(null); // File
  const [photoPreview, setPhotoPreview] = useState("");
  const fileInputRef = useRef(null);
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const change = (k, v) => setForm({ ...form, [k]: v });

  const onPickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];
    if (!allowed.includes(f.type)) {
      toast.error("Only JPG, PNG, or WebP allowed");
      return;
    }
    if (f.size > 5 * 1024 * 1024) {
      toast.error("Image must be under 5MB");
      return;
    }
    setPhoto(f);
    const reader = new FileReader();
    reader.onload = () => setPhotoPreview(reader.result);
    reader.readAsDataURL(f);
  };

  const clearPhoto = () => {
    setPhoto(null);
    setPhotoPreview("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.name || !form.email || !form.password) { setError("Name, email and password are required"); return; }
    if (!photo) { setError("Please upload a profile photo"); return; }
    if (form.password.length < 8) { setError("Password must be at least 8 characters"); return; }
    if (form.password !== form.confirm) { setError("Passwords do not match"); return; }
    if (!form.accept_terms) { setError("Please accept the Terms & Privacy Policy"); return; }
    if (form.dob) {
      const age = (Date.now() - new Date(form.dob).getTime()) / (365.25 * 24 * 3600 * 1000);
      if (age < 18) { setError("You must be 18 or older to join"); return; }
    }
    setLoading(true);
    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        password: form.password,
        dob: form.dob || null,
        city: form.city.trim(),
        service_mode: form.service_mode || null,
        accept_terms: true,
      };
      const r = await api.post("/auth/register", payload);
      // Upload mandatory profile photo (session cookie is now set)
      try {
        const fd = new FormData();
        fd.append("picture", photo);
        const pr = await api.post("/profile/upload-picture", fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (pr.data?.picture) {
          r.data.user.picture = pr.data.picture;
        }
      } catch (uerr) {
        toast.error("Account created but photo upload failed — you can add it from your profile.");
      }
      setUser(r.data.user);
      await refresh();
      toast.success("Account created! Please verify your email.");
      navigate("/verify-email", { state: { email: payload.email } });
    } catch (err) {
      setError(fmtDetail(err.response?.data?.detail) || "Signup failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4 py-10">
      <div className="w-full max-w-xl">
        <div className="mb-6">
          <Logo size="lg" />
        </div>

        <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 sm:p-8">
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-slate-900">Create your account</h1>
          <p className="text-slate-500 mt-1 text-sm">Join MyLiveFriend and start meeting your pincode.</p>

          <form onSubmit={submit} className="mt-6 space-y-4" data-testid="signup-form">
            {/* Profile Photo (mandatory) */}
            <div className="flex items-center gap-4 p-4 rounded-xl border-2 border-dashed border-slate-200">
              <div className="relative">
                {photoPreview ? (
                  <>
                    <img src={photoPreview} alt="preview" className="w-20 h-20 rounded-full object-cover border-2 border-teal-500" data-testid="signup-photo-preview" />
                    <button
                      type="button"
                      onClick={clearPhoto}
                      className="absolute -top-1 -right-1 w-6 h-6 bg-rose-500 text-white rounded-full flex items-center justify-center hover:bg-rose-600"
                      aria-label="Remove photo"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <div className="w-20 h-20 rounded-full bg-slate-100 border-2 border-dashed border-slate-300 flex items-center justify-center text-slate-400">
                    <Camera className="w-7 h-7" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <Label className="font-semibold">Profile Photo <span className="text-rose-500">*</span></Label>
                <p className="text-xs text-slate-500 mt-0.5">JPG, PNG or WebP. Max 5MB. Helps neighbours recognise you.</p>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={onPickPhoto}
                  className="hidden"
                  data-testid="signup-photo-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 rounded-full border-teal-300 text-teal-700 hover:bg-teal-50"
                  data-testid="signup-photo-pick-btn"
                >
                  <Camera className="w-4 h-4 mr-2" /> {photo ? "Change photo" : "Upload photo"}
                </Button>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <Label>Full Name <span className="text-rose-500">*</span></Label>
                <div className="relative mt-1">
                  <User className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input data-testid="signup-name" value={form.name} onChange={(e) => change("name", e.target.value)} className="pl-9" placeholder="Rahul Shah" />
                </div>
              </div>
              <div>
                <Label>Email Address <span className="text-rose-500">*</span></Label>
                <div className="relative mt-1">
                  <Mail className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input data-testid="signup-email" type="email" value={form.email} onChange={(e) => change("email", e.target.value)} className="pl-9" placeholder="you@example.com" />
                </div>
              </div>
              <div>
                <Label>Mobile Number</Label>
                <div className="relative mt-1">
                  <Phone className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input data-testid="signup-phone" value={form.phone} onChange={(e) => change("phone", e.target.value)} className="pl-9" placeholder="+91 98765 43210" />
                </div>
              </div>
              <div>
                <Label>Date of Birth</Label>
                <div className="relative mt-1">
                  <Calendar className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  <Input data-testid="signup-dob" type="date" value={form.dob} onChange={(e) => change("dob", e.target.value)} className="pl-9" />
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label>City</Label>
                <div className="relative mt-1">
                  <MapPin className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input data-testid="signup-city" value={form.city} onChange={(e) => change("city", e.target.value)} className="pl-9" placeholder="Mumbai" />
                </div>
              </div>
              <div className="sm:col-span-2">
                <Label>I'm here to <span className="text-slate-400 text-xs">(optional)</span></Label>
                <div className="mt-2 grid grid-cols-3 gap-2">
                  {SERVICE_MODES.map((m) => {
                    const active = form.service_mode === m.id;
                    return (
                      <button key={m.id} type="button" data-testid={`signup-mode-${m.id}`} onClick={() => change("service_mode", active ? "" : m.id)} className={`rounded-xl border-2 p-3 text-left transition ${active ? "border-teal-500 bg-teal-50 ring-2 ring-teal-100" : "border-slate-200 hover:border-slate-300"}`}>
                        <m.icon className={`w-4 h-4 ${active ? "text-teal-700" : "text-slate-500"}`} />
                        {/* ME ON RENT */}
                        <div className="font-heading font-bold text-sm mt-1 text-slate-900">{m.label}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-1">{m.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div>
                <Label>Password <span className="text-rose-500">*</span></Label>
                <div className="relative mt-1">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input data-testid="signup-password" type={showPass ? "text" : "password"} value={form.password} onChange={(e) => change("password", e.target.value)} className="pl-9 pr-10" placeholder="Min 8 characters" />
                  <button type="button" onClick={() => setShowPass(!showPass)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {showPass ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <div>
                <Label>Confirm Password <span className="text-rose-500">*</span></Label>
                <div className="relative mt-1">
                  <Lock className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input data-testid="signup-confirm" type={showPass ? "text" : "password"} value={form.confirm} onChange={(e) => change("confirm", e.target.value)} className="pl-9" placeholder="Repeat password" />
                </div>
              </div>
            </div>

            <div className="flex items-start gap-2 pt-2">
              <Checkbox id="terms" checked={form.accept_terms} onCheckedChange={(v) => change("accept_terms", !!v)} data-testid="signup-accept-terms" />
              <Label htmlFor="terms" className="text-sm font-normal cursor-pointer leading-relaxed">
                I agree to the <Link to="/about" className="font-semibold text-teal-700">Terms</Link> & <Link to="/about" className="font-semibold text-teal-700">Privacy Policy</Link>, and confirm I am 18+.
              </Label>
            </div>

            {error && (<div data-testid="signup-error" className="rounded-lg bg-rose-50 border border-rose-200 text-rose-700 text-sm px-3 py-2">{error}</div>)}

            <Button data-testid="signup-submit" type="submit" disabled={loading} className="w-full h-11 bg-teal-600 hover:bg-teal-700 text-white rounded-full font-semibold">
              {loading ? "Creating account..." : "Create Account"}
            </Button>

            <p className="text-center text-sm text-slate-600">
              Already have an account? <Link to="/login" className="font-semibold text-teal-700">Login</Link>
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
